// src/types/tasks.ts

export interface BaseTaskMetadata {
	houseId: number;
	tags: string[] | null;
	batchId?: string; // Optional batch ID for grouping tasks

	/** the following fields are used for retry logic, and set in the HouseTaskContextManager. TODO: review them */
	error?: string;
	retryCount?: number;
	maxRetries?: number;
}

export interface MoveTaskMetadata extends BaseTaskMetadata {
	documentIds: number[];
	srcBucket: string;
	dstBucket: string;
}

export interface UploadTaskMetadata extends BaseTaskMetadata {
	filename: string;
	fileSize?: number;
}

export interface UpdateDocumentTaskMetadata extends BaseTaskMetadata {
	documentId: number;
	filename: string;
}

export type GenerateMaintenanceScheduleTaskMetadata = BaseTaskMetadata;

export type TaskMetadata = UploadTaskMetadata | UpdateDocumentTaskMetadata | MoveTaskMetadata | Record<string, unknown>;

export type MoveDocumentTask = Task & { metadata: MoveTaskMetadata };
export type GenerateMaintenanceScheduleTask = Task & { metadata: GenerateMaintenanceScheduleTaskMetadata };

/**
 * `paused` is terminal but not an outcome the user caused: an upload allowance is spent and refills
 * over hours, so the work stops without having failed. Distinct from `failed` on purpose — carrying
 * it as a flag on `failed` would make the status contradict its own metadata, and a retry control
 * would be offered for something no retry can fix.
 */
export const TaskStatus = {
	Queued: 'queued',
	Pending: 'pending',
	InProgress: 'in-progress',
	Completed: 'completed',
	Failed: 'failed',
	Skipped: 'skipped',
	Cancelled: 'cancelled',
	Paused: 'paused',
} as const;
export type TaskStatus = (typeof TaskStatus)[keyof typeof TaskStatus];

/**
 * API response structure from /houses/{house_id}/tasks endpoint
 */
export interface ApiTask {
	task_id: string;
	status: 'pending' | 'completed' | 'in_progress' | 'failed';
	progress: number;
	metadata: {
		resource_id: string;
		task_type: string;
		created_at: string;
		ttl: string;
		allow_concurrent: string;
		instance_id: string;
		[key: string]: unknown; // Allow additional metadata fields
	};
}

export interface Task {
	id: string; // Unique task ID
	type: string; // Task type, e.g., "upload", "backgroundProcess", etc.
	status: TaskStatus;
	metadata: TaskMetadata; // Arbitrary metadata for the task
	expiresAt?: number;
	pending?: boolean; // Whether the task should start polling immediately after creation or not
	progress?: number;
	isPollingTask?: boolean; // Whether the task should be polled for status or not
	createdAt?: string;
}

/**
 * Utility type for the API response array
 */
export type ApiTasksResponse = ApiTask[];
